import Timeline from "@mui/lab/Timeline";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from "@mui/lab/TimelineOppositeContent";
import { Box, Paper, Typography } from "@mui/material";

import { useOrderContext } from "contexts/OrderContext";
import { IssueTypeEnum, orderStatus, userTypes } from "enums/shared";
import { useRTL } from "hooks/isRTL";
import formatDateForInput from "utils/formatDateForInput";
import { fallbackValue } from "constants/fallbackConstant";

/**
 * Displays a timeline of events related to the order, including status changes,
 * user interactions, and notes.
 */
const OrderLogsTab = () => {
  const { orderLogs, logsLoading, logsError, orderIssues } = useOrderContext();
  const { t, isRTL } = useRTL();

  if (logsLoading) return <p>Loading order logs...</p>;
  if (logsError) return <p>Error fetching order logs.</p>;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        gap: "1.5rem",
        padding: "1.5rem",
      }}
    >
      <Paper
        elevation={2}
        sx={{
          minWidth: "300px",
          flex: 1,
          padding: "1.5rem",
          backgroundColor: "background.white",
          color: "background.dark",
        }}
      >
        <Timeline
          sx={{
            [`& .${timelineOppositeContentClasses.root}`]: {
              flex: 0.7,
              textAlign: isRTL ? "left" : "right",
            },
          }}
        >
          {orderLogs.map(({ id, creationDate, newStatus, note, userType }) => (
            <TimelineItem key={id}>
              <TimelineOppositeContent color="textSecondary">
                <Typography variant="body2">
                  {formatDateForInput(creationDate)}
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ textAlign: isRTL ? "right" : "left", lineHeight: 2 }}
                  >
                    <strong>{t("status")}:</strong>{" "}
                    {t(orderStatus[newStatus]?.value) || fallbackValue}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ textAlign: isRTL ? "right" : "left", lineHeight: 2 }}
                  >
                    <strong>{t("userType")}:</strong>{" "}
                    {t(userTypes[userType]) || fallbackValue}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ textAlign: isRTL ? "right" : "left", lineHeight: 2 }}
                  >
                    {" "}
                    <strong>{t("note")}</strong> {t(note) || fallbackValue}
                  </Typography>
                </Box>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Paper>
      <Paper
        elevation={2}
        sx={{
          minWidth: "300px",
          flex: 1,
          padding: "1.5rem",
          backgroundColor: "background.white",
          color: "background.dark",
        }}
      >
        <h3
          style={{
            textAlign: isRTL ? "right" : "left",
            fontSize: "1.125rem",
            fontWeight: 500,
          }}
        >
          {t("issuesAndSolutions")}
        </h3>
        <Timeline
          sx={{
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
              textAlign: isRTL ? "left" : "right",
            },
          }}
        >
          {orderIssues.map(({ id, modificationDate, type, resolveNote }) => (
            <>
              <TimelineItem key={id}>
                <TimelineSeparator>
                  <TimelineDot />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent>
                  {" "}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: isRTL ? "right" : "left",
                        lineHeight: 2,
                        fontWeight: 200,
                      }}
                    >
                      <strong>{t("issueType")}: </strong>{" "}
                      {t(IssueTypeEnum[type]?.value) || fallbackValue}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: isRTL ? "right" : "left",
                        lineHeight: 2,
                        fontWeight: 200,
                      }}
                    >
                      <strong>{t("dateAndTime")}: </strong>{" "}
                      {formatDateForInput(modificationDate)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: isRTL ? "right" : "left",
                        lineHeight: 2,
                        fontWeight: 200,
                      }}
                    >
                      {" "}
                      <strong>{t("adminSolution")}: </strong>{" "}
                      {resolveNote || fallbackValue}
                    </Typography>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            </>
          ))}
        </Timeline>
      </Paper>

      {/* <Box
        sx={{
          visibility: "hidden",
          flex: 1,
        }}
      >
        <Paper
          elevation={2}
          sx={{
            padding: "1.5rem",
            minWidth: "300px",
          }}
        />
      </Box> */}
    </Box>
  );
};

export default OrderLogsTab;
